import { escapeHtml, INPUT_CLS, BTN_CLS } from '../admin-ui';

/** Markup helpers shared by the admin dialogs. */

export function textField(
  name: string,
  label: string,
  value = '',
  opts: { type?: string; required?: boolean; placeholder?: string; hint?: string } = {},
): string {
  return `
    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-text-dim">${escapeHtml(label)}</span>
      <input name="${name}" type="${opts.type ?? 'text'}" value="${escapeHtml(value)}"
        ${opts.required ? 'required' : ''} ${opts.placeholder ? `placeholder="${escapeHtml(opts.placeholder)}"` : ''}
        autocomplete="off" class="${INPUT_CLS}" />
      ${opts.hint ? `<span class="text-[10px] text-text-dim leading-relaxed">${escapeHtml(opts.hint)}</span>` : ''}
    </label>`;
}

export function selectField(
  name: string,
  label: string,
  options: readonly string[],
  selected?: string,
  labels?: Record<string, string>,
): string {
  return `
    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-text-dim">${escapeHtml(label)}</span>
      <select name="${name}" class="${INPUT_CLS}">
        ${options
          .map(
            (o) =>
              `<option value="${escapeHtml(o)}" ${o === selected ? 'selected' : ''}>${escapeHtml(labels?.[o] ?? o)}</option>`,
          )
          .join('')}
      </select>
    </label>`;
}

export function checkboxField(name: string, label: string, checked: boolean): string {
  return `
    <label class="flex items-center gap-2 min-h-11">
      <input name="${name}" type="checkbox" ${checked ? 'checked' : ''} class="w-4 h-4 accent-gold" />
      <span class="text-sm text-text">${escapeHtml(label)}</span>
    </label>`;
}

export function submitRow(label: string, formName: string): string {
  return `
    <div class="flex justify-end gap-2 pt-1">
      <button type="button" data-action="close" class="min-h-11 px-4 text-sm text-text-dim hover:text-text touch-manipulation">Cancel</button>
      <button type="submit" form="${formName}" class="${BTN_CLS}">${escapeHtml(label)}</button>
    </div>`;
}

/** Reads a form into a plain object, trimming strings and dropping blanks. */
export function readForm(form: HTMLFormElement): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  const data = new FormData(form);
  for (const [k, v] of data.entries()) {
    out[k] = typeof v === 'string' ? v.trim() : true;
  }
  // Unchecked boxes are absent from FormData entirely, so they have to be
  // read back off the elements or "turn verification off" would be a no-op.
  form.querySelectorAll('input[type="checkbox"]').forEach((el) => {
    const box = el as HTMLInputElement;
    out[box.name] = box.checked;
  });
  return out;
}

export function errorText(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const anyErr = err as { response?: { data?: { message?: unknown } }; message?: string };
    const msg = anyErr.response?.data?.message;
    if (Array.isArray(msg)) return String(msg[0]);
    if (typeof msg === 'string') return msg;
    if (anyErr.message) return anyErr.message;
  }
  return fallback;
}
